import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  TablePagination,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import EditIcon from "@mui/icons-material/Edit";
import CommissionEditModal from "./CommissionEditModal";
import { useSelector } from "react-redux";
import { useFetch } from "../../hooks";
import { urls } from "../../urls/urls";
import { BASE_URL_TEST } from "../../utils/baseUrl";

const statusColors = {
  Pending: "#FFF3E0",
  "Accept pending": "#BBDEFB",
  Submitted: "#C8E6C9",
  pending: "#FFF3E0",
  completed: "#C8E6C9",
  failed: "#FFCDD2",
};

export default function CommissionTable() {
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [agentData, setAgentData] = useState([]);
  const apptoken = useSelector((state) => state.user.apptoken);

  const { data, isLoading, fetchData } = useFetch(
    BASE_URL_TEST + urls("getAllWithdrawls"),
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: apptoken ? `Bearer ${apptoken}` : "",
      },
    },
  );

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data && data.status) {
      setAgentData(data.data || []);
    }
  }, [data]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Calculate summary data from API response
  const totalAgents = new Set(agentData.map((w) => w.agent_id)).size;
  const totalAmount = agentData.reduce(
    (sum, w) => sum + parseFloat(w.amount || 0),
    0,
  );
  const completedAmount = agentData
    .filter((w) => w.status === "completed")
    .reduce((sum, w) => sum + parseFloat(w.amount || 0), 0);
  const pendingAmount = agentData
    .filter((w) => w.status === "pending")
    .reduce((sum, w) => sum + parseFloat(w.amount || 0), 0);

  const summaryData = [
    {
      title: "Total Agents",
      value: totalAgents,
      growth: `${totalAgents} Agents`,
      color: "#4CAF50",
      icon: <PeopleIcon fontSize="large" />,
    },
    {
      title: "Total Amount",
      value: formatCurrency(totalAmount),
      growth: "Total Amount",
      color: "#1E88E5",
      icon: <AccountBalanceWalletIcon fontSize="large" />,
    },
    {
      title: "Total Hand Over Amount",
      value: formatCurrency(completedAmount),
      growth: `${agentData.filter((w) => w.status === "completed").length} Completed`,
      color: "#26A69A",
      icon: <LocalAtmIcon fontSize="large" />,
    },
    {
      title: "Total Pending Amount",
      value: formatCurrency(pendingAmount),
      growth: `${agentData.filter((w) => w.status === "pending").length} Pending`,
      color: "#FF9800",
      icon: <PendingActionsIcon fontSize="large" />,
    },
  ];

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const getAgentName = (agentDetails) => {
    if (!agentDetails) return "N/A";
    const firstName = agentDetails.first_name || "";
    const lastName = agentDetails.last_name || "";
    return `${firstName} ${lastName}`.trim() || "N/A";
  };

  const filteredOrders = agentData.filter(
    (order) =>
      order.agent_id
        ?.toString()
        .toLowerCase()
        .includes(searchValue.toLowerCase()) ||
      getAgentName(order.agentDetails)
        .toLowerCase()
        .includes(searchValue.toLowerCase()),
  );

  const handleEditClick = (commission) => {
    setSelectedCommission(commission);
    setOpenEditModal(true);
  };

  const handleCloseModal = () => {
    setOpenEditModal(false);
    setSelectedCommission(null);
  };

  const handleUpdateSuccess = () => {
    fetchData();
  };

  const getStatusDisplay = (status) => {
    if (status === "pending") return "Pending";
    if (status === "completed") return "Submitted";
    if (status === "failed") return "Failed";
    return status;
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
          bgcolor: "#EEF2FF",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        bgcolor: "#EEF2FF",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Grid container spacing={1}>
        {summaryData.map((item, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                bgcolor: item.color,
                color: "#fff",
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  justifyContent={"space-between"}
                >
                  <Box>
                    <Typography variant="body2">{item.title}</Typography>
                    <Typography variant="h6">{item.value}</Typography>
                    <Typography variant="body2">{item.growth}</Typography>
                  </Box>
                  <Typography variant="h3">{item.icon}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          mt: 2,
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Agent Commission
        </Typography>
        <TextField
          placeholder="Search by Agent ID"
          variant="outlined"
          size="small"
          value={searchValue}
          onChange={handleSearchChange}
          sx={{
            width: "250px",
            borderRadius: "10px",
            backgroundColor: "#f5f7fa",
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              "& fieldset": {
                borderColor: "#d8dbe2",
              },
              "&:hover fieldset": {
                borderColor: "#aeb3bb",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#aeb3bb",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "#8b94a3" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer
        component={Paper}
        sx={{ borderRadius: "20px", boxShadow: 1 }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#F5F5F5", fontWeight: "bold" }}>
              <TableCell sx={{ fontWeight: "bold" }}>AGENT ID</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>AGENT NAME</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>TOTAL AMOUNT</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>HANDOVER AMOUNT</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>PENDING AMOUNT</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>STATUS</TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="right">
                ACTION
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body1" sx={{ py: 4, color: "#666" }}>
                    No agent commission data found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredOrders
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((order, index) => (
                  <TableRow key={order.payment_id || index}>
                    <TableCell>{order.agent_id || "N/A"}</TableCell>
                    <TableCell>{getAgentName(order.agentDetails)}</TableCell>
                    <TableCell>
                      {formatCurrency(parseFloat(order.amount || 0))}
                    </TableCell>
                    <TableCell>
                      {order.status === "completed"
                        ? formatCurrency(parseFloat(order.amount || 0))
                        : formatCurrency(0)}
                    </TableCell>
                    <TableCell>
                      {order.status === "pending"
                        ? formatCurrency(parseFloat(order.amount || 0))
                        : formatCurrency(0)}
                    </TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{
                          backgroundColor:
                            statusColors[order.status] || "#FFF3E0",
                          borderRadius: "20px",
                          padding: "6px 16px",
                          color:
                            order.status === "pending"
                              ? "#FFA726"
                              : order.status === "completed"
                                ? "#4CAF50"
                                : "#F44336",
                          fontWeight: "500",
                          display: "inline-block",
                        }}
                      >
                        {getStatusDisplay(order.status)}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditClick(order)}
                        sx={{
                          textTransform: "none",
                          borderColor: "#2c666e",
                          color: "#2c666e",
                          borderRadius: "8px",
                          "&:hover": {
                            borderColor: "#245757",
                            bgcolor: "#f5f5f5",
                          },
                        }}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={filteredOrders.length}
          page={page}
          rowsPerPageOptions={[5, 10, 25]}
          rowsPerPage={rowsPerPage}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) =>
            setRowsPerPage(parseInt(event.target.value, 10))
          }
          sx={{ p: 2, bgcolor: "#F5F5F5", borderTop: "1px solid #E0E0E0" }}
        />
      </TableContainer>

      <CommissionEditModal
        open={openEditModal}
        onClose={handleCloseModal}
        commissionData={selectedCommission}
        onSuccess={handleUpdateSuccess}
      />
    </Box>
  );
}
